import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './AdminAds.css';

const API_URL = process.env.REACT_APP_API_URL;

const AdminAds = ({ token, user }) => {
  const navigate = useNavigate();
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, pending_payment, paid, active, inactive, rejected
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user?.isAdmin) {
      navigate('/app');
      return;
    }
    fetchAds();
  }, [user]);

  const fetchAds = async () => {
    try {
      const response = await fetch(`${API_URL}/admin/ads`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();

      if (response.ok) {
        setAds(data.ads);
      } else {
        setError(data.error || 'Failed to fetch ads');
      }
    } catch (err) {
      setError('Network error');
    } finally {
      setLoading(false);
    }
  };

  const updateAdStatus = async (adId, status) => {
    if (!window.confirm(`Are you sure you want to ${status} this ad?`)) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/admin/ads/${adId}/status`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status })
      });

      if (response.ok) {
        fetchAds(); // Refresh list
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to update status');
      }
    } catch (err) {
      alert('Network error');
    }
  };

  const filteredAds = ads.filter(ad => {
    if (filter === 'all') return true;
    return ad.status === filter;
  });

  const getStatusBadge = (status) => {
    const badges = {
      'pending_payment': { label: 'Pending Payment', class: 'status-pending' },
      'paid': { label: 'Awaiting Review', class: 'status-paid' },
      'active': { label: 'Active', class: 'status-active' },
      'inactive': { label: 'Inactive', class: 'status-inactive' },
      'rejected': { label: 'Rejected', class: 'status-rejected' }
    };

    const badge = badges[status] || { label: status, class: 'status-unknown' };
    return <span className={`status-badge ${badge.class}`}>{badge.label}</span>;
  };

  if (loading) {
    return <div className="admin-loading">Loading ads...</div>;
  }

  return (
    <div className="admin-ads-page">
      <div className="admin-header">
        <h1>Ad Management Dashboard</h1>
        <button onClick={() => navigate('/app')} className="back-btn">
           Back to App
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Stats Overview */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-value">{ads.length}</div>
          <div className="stat-label">Total Ads</div>
        </div>
        <div className="stat-card active">
          <div className="stat-value">{ads.filter(a => a.status === 'active').length}</div>
          <div className="stat-label">Active</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{ads.filter(a => a.status === 'paid').length}</div>
          <div className="stat-label">Pending Review</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">
            {ads.reduce((sum, ad) => sum + (ad.clicks || 0), 0)}
          </div>
          <div className="stat-label">Total Clicks</div>
        </div>
      </div>

      {/* Filters */}
      <div className="filter-bar">
        <button
          className={filter === 'all' ? 'active' : ''}
          onClick={() => setFilter('all')}
        >
          All ({ads.length})
        </button>
        <button
          className={filter === 'paid' ? 'active' : ''}
          onClick={() => setFilter('paid')}
        >
          Pending Review ({ads.filter(a => a.status === 'paid').length})
        </button>
        <button
          className={filter === 'active' ? 'active' : ''}
          onClick={() => setFilter('active')}
        >
          Active ({ads.filter(a => a.status === 'active').length})
        </button>
        <button
          className={filter === 'inactive' ? 'active' : ''}
          onClick={() => setFilter('inactive')}
        >
          Inactive ({ads.filter(a => a.status === 'inactive').length})
        </button>
      </div>

      {/* Ads Table */}
      <div className="ads-table-container">
        <table className="ads-table">
          <thead>
            <tr>
              <th>Preview</th>
              <th>Company</th>
              <th>Contact</th>
              <th>Plan</th>
              <th>Status</th>
              <th>Days Left</th>
              <th>Impressions</th>
              <th>Clicks</th>
              <th>CTR</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredAds.length === 0 ? (
              <tr>
                <td colSpan="10" className="no-data">No ads found</td>
              </tr>
            ) : (
              filteredAds.map(ad => (
                <tr key={ad.id}>
                  <td>
                    <img
                      src={`data:${ad.imageMimeType};base64,${ad.imageData}`}
                      alt={ad.companyName}
                      className="ad-preview"
                    />
                  </td>
                  <td>
                    <div className="company-cell">
                      <strong>{ad.companyName}</strong>
                      <a href={ad.targetUrl} target="_blank" rel="noopener noreferrer" className="ad-url">
                        {ad.targetUrl}
                      </a>
                    </div>
                  </td>
                  <td>
                    <div className="contact-cell">
                      <div>{ad.email}</div>
                      {ad.phone && <div className="phone">{ad.phone}</div>}
                    </div>
                  </td>
                  <td>
                    <span className="plan-badge">
                      {ad.plan === '6months' ? '6 Months' : '12 Months'}
                    </span>
                  </td>
                  <td>{getStatusBadge(ad.status)}</td>
                  <td>
                    {ad.daysRemaining !== null ? (
                      <span className={ad.daysRemaining < 30 ? 'expiring-soon' : ''}>
                        {ad.daysRemaining} days
                      </span>
                    ) : (
                      <span className="na">N/A</span>
                    )}
                  </td>
                  <td className="metric-cell">{ad.impressions || 0}</td>
                  <td className="metric-cell">{ad.clicks || 0}</td>
                  <td className="metric-cell">{ad.ctr}%</td>
                  <td>
                    <div className="action-buttons">
                      {ad.status === 'paid' && (
                        <>
                          <button
                            className="approve-btn"
                            onClick={() => updateAdStatus(ad.id, 'active')}
                          >
                             Approve
                          </button>
                          <button
                            className="reject-btn"
                            onClick={() => updateAdStatus(ad.id, 'rejected')}
                          >
                             Reject
                          </button>
                        </>
                      )}
                      {ad.status === 'active' && (
                        <button
                          className="deactivate-btn"
                          onClick={() => updateAdStatus(ad.id, 'inactive')}
                        >
                          Deactivate
                        </button>
                      )}
                      {ad.status === 'inactive' && (
                        <button
                          className="activate-btn"
                          onClick={() => updateAdStatus(ad.id, 'active')}
                        >
                          Activate
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminAds;
